import { memo, useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import CurrencyContext from 'contexts/currencyContext'
import Group from 'components/utils/Group'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import NestableClickable from 'components/Common/NestableClickable'
import SearchTileRowInclusionItem from 'components/SearchKit/SearchTiles/shared/SearchTileRowInclusionItem'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import LineExternalLinkAltIcon from 'LuxKit/Icons/line/LineExternalLinkAltIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import PriceDetails from 'tripPlanner/components/ItemOfferPrice/PriceDetails'
import type { DrawerHotelTileProps, DrawerHotelInclusion } from './types'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import useWarnMissingCurrency from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useWarnMissingCurrency'

const INCLUSIONS_TRUNCATE_AFTER = 5
const AMENITIES_TRUNCATE_AFTER = 6

const TileContainer = styled(NestableClickable)`
  display: grid;
  grid-template-columns: 1fr;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    grid-template-columns: ${rem(300)} 1fr;
  }
`

const ImageSection = styled.div`
  position: relative;
  height: ${rem(200)};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    height: 100%;
    min-height: ${rem(250)};
  }
`

const TileStatusContainer = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
  z-index: 1;
`

const ContentSection = styled(Group)`
  padding: ${rem(20)};
`

const TitleGroup = styled(Group)`
  flex: 1;
  min-width: 0;
`

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    margin-top: auto;
  }
`

const AmenitiesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(4)} ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

interface InclusionItemProps {
  inclusion: DrawerHotelInclusion
}

function InclusionItem({ inclusion }: InclusionItemProps) {
  return (
    <OfferInclusionItem inclusion={inclusion} lineClamp={1} />
  )
}

function DrawerHotelTile({
  hotel,
  isBooked = false,
  onSelectHotel,
  position,
  trackingOffer,
  itineraryData,
  contextStop,
  onSendMessage,
  onAfterAction,
  onShowOnMap,
  filterNights,
}: DrawerHotelTileProps) {
  const intl = useIntl()
  const currentCurrency = useContext(CurrencyContext)
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [isShowingAllInclusions, setIsShowingAllInclusions] = useState(false)
  const hasFiredImpressionRef = useRef(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  const {
    name,
    location,
    images,
    rating,
    inclusions,
    tileAmenities,
    price,
    luxPlusPrice,
    deeplinkUrl,
    mapUrl,
  } = hotel

  useEffect(() => {
    if (!inView || !trackingOffer || position === undefined || hasFiredImpressionRef.current) {
      return
    }

    hasFiredImpressionRef.current = true

    dispatchOfferListEvent({
      type: OfferListEvents.productImpression,
      offer: trackingOffer,
      position,
    })
  }, [dispatchOfferListEvent, inView, position, trackingOffer])

  const missingCurrencyContext = useMemo(() => ({ hotelId: hotel?.id }), [hotel?.id])
  useWarnMissingCurrency(
    Boolean(price && !price.currency),
    '[TripChat] Hotel price missing currency',
    missingCurrencyContext,
  )

  const handleSelectHotel = useCallback(() => {
    if (trackingOffer && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: trackingOffer,
        position,
      })
    }

    onSelectHotel(hotel)
  }, [dispatchOfferListEvent, hotel, onSelectHotel, position, trackingOffer])

  const handleShowOnMapClick = useCallback(() => {
    onShowOnMap?.(hotel)
  }, [onShowOnMap, hotel])

  const visibleInclusions = useMemo(
    () => isShowingAllInclusions
      ? (inclusions ?? [])
      : (inclusions ?? []).slice(0, INCLUSIONS_TRUNCATE_AFTER),
    [inclusions, isShowingAllInclusions],
  )
  const hiddenInclusionsCount = Math.max(0, (inclusions ?? []).length - INCLUSIONS_TRUNCATE_AFTER)
  const handleShowMoreInclusions = useCallback(() => {
    setIsShowingAllInclusions(true)
  }, [])
  const handleShowLessInclusions = useCallback(() => {
    setIsShowingAllInclusions(false)
  }, [])
  const visibleAmenities = useMemo(
    () => (tileAmenities ?? []).slice(0, AMENITIES_TRUNCATE_AFTER),
    [tileAmenities],
  )
  const shouldShowMapLink = !!mapUrl && !!onShowOnMap
  const priceLabel = price
    ? intl.formatMessage(
        {
          id: 'crlO0U--oVDW84QsWyzI',
          defaultMessage: '{count, plural, one {# night} other {# nights}} from',
          description: 'Hotel tile price caption showing number of nights in the trip planner AI chat',
        },
        { count: price.nights },
      )
    : undefined

  return (
    <TileContainer ref={inViewRef} data-testid="drawer-hotel-tile" onClick={handleSelectHotel}>
      <ImageSection>
        {isBooked && (
          <TileStatusContainer>
            <BookedLabel />
          </TileStatusContainer>
        )}
        {onSendMessage && !isBooked && (
          <TileStatusContainer>
            <AddHotelToTripButton
              itineraryData={itineraryData}
              hotelName={name}
              offerId={hotel.offerId}
              offerType={hotel.offerType}
              hotelCity={location}
              hotelCoordinates={hotel.geoLocation}
              contextStop={contextStop}
              onSendMessage={onSendMessage}
              onAfterAction={onAfterAction}
              filterNights={filterNights}
            />
          </TileStatusContainer>
        )}
        <ImageCarousel
          images={images}
          arrowBehaviour="interaction-visible"
          arrowStyle="ghost"
        />
      </ImageSection>

      <ContentSection direction="vertical" gap={16}>
        <Group
          direction="horizontal"
          gap={12}
          horizontalAlign="space-between"
          verticalAlign="start"
        >
          <TitleGroup direction="vertical" gap={4}>
            <Group direction="horizontal" gap={8} verticalAlign="center">
              <BodyText variant="small" colour="light-foreground-alt">
                {location}
              </BodyText>
              {shouldShowMapLink && (
                <>
                  <BodyText variant="small" colour="light-foreground-alt">{intl.formatMessage(middleDotSeparatorMessage)}</BodyText>
                  <TextLink size="small" onClick={handleShowOnMapClick}>
                    {intl.formatMessage({
                      id: 'cjO2hzRshkBMda0Hg20K',
                      defaultMessage: 'Show on map',
                      description: 'Link to show hotel location on map on the hotel or room booking page',
                    })}
                  </TextLink>
                </>
              )}
            </Group>
            <Heading variant="heading5" lineClamp={2}>
              {name}
            </Heading>
          </TitleGroup>

          {rating !== undefined && rating > 0 && (
            <NumberRating
              rating={rating}
              onlyNumber
              variant="small"
              squareContainer
            />
          )}
        </Group>

        {visibleInclusions.length > 0 && (
          <Group direction="vertical" gap={4}>
            {visibleInclusions.map((inclusion) => (
              <InclusionItem key={inclusion.id} inclusion={inclusion} />
            ))}
            {!isShowingAllInclusions && hiddenInclusionsCount > 0 && (
              <TextLink size="medium" onClick={handleShowMoreInclusions}>
                {intl.formatMessage(
                  {
                    id: 'T7vrcmV7MnW1J_G47i0o',
                    defaultMessage: '+{count} more',
                    description: 'Link showing count of additional hidden hotel inclusions',
                  },
                  { count: hiddenInclusionsCount },
                )}
              </TextLink>
            )}
            {isShowingAllInclusions && hiddenInclusionsCount > 0 && (
              <TextLink size="medium" onClick={handleShowLessInclusions}>
                {intl.formatMessage({
                  id: 'xDnpVVOBV5A9HwgjkZ3R',
                  defaultMessage: 'Show less',
                  description: 'Link to collapse expanded hotel inclusions on drawer hotel tile',
                })}
              </TextLink>
            )}
          </Group>
        )}
        {visibleInclusions.length === 0 && visibleAmenities.length > 0 && (
          <AmenitiesGrid>
            {visibleAmenities.map((amenity) => (
              <SearchTileRowInclusionItem
                key={amenity.id}
                inclusion={amenity}
                lineClamp={1}
              />
            ))}
          </AmenitiesGrid>
        )}

        <PriceRow>
          {!!price && priceLabel && (
            <PriceDetails
              price={price.total}
              luxPlusPrice={luxPlusPrice}
              currencyCode={price.currency ?? currentCurrency}
              priceLabel={priceLabel}
              showTaxesAndFees={false}
            />
          )}
          {!price && !!deeplinkUrl && (
            <TextLink
              size="medium"
              href={deeplinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<LineExternalLinkAltIcon />}
            >
              {intl.formatMessage({
                id: 'TM5M7ZAPmTq1YXDuSEVJ',
                defaultMessage: 'Pricing',
                description: 'Link to view hotel pricing details on the hotel or room booking page',
              })}
            </TextLink>
          )}

          <TextButton
            kind="primary"
            size="medium"
            onClick={handleSelectHotel}
          >
            {intl.formatMessage({
              id: '0cKWSwyDZvKU0-9U6UrT',
              defaultMessage: 'View offer',
              description: 'Button to view hotel offer details on the hotel or room booking page',
            })}
          </TextButton>
        </PriceRow>
      </ContentSection>
    </TileContainer>
  )
}

export default memo(DrawerHotelTile)
